// English is the source dictionary: every other locale has exactly these keys.
// Any change here updates es.ts and de.ts in the same change.
//
// Messages use vue-i18n syntax. Keep to plain text and `{name}` placeholders in
// the keys the server reads (`meta`, `home`, `gallery`, `pricing`, `pageShell`):
// the Cloudflare middleware fills them with `formatMessage`, not vue-i18n. The
// gallery project about panel (`galleryProject.about`) is also rendered on the
// server, so the same rule applies there.
// Platform, library, and plan names stay untranslated in every locale.
const en = {
    languageSwitcher: {
        label: 'Language',
    },
    // Navigation bar and the shared bits of the public pages.
    nav: {
        tagline: 'Embedded GUI design toolkit',
        logoAlt: 'Lopaka Logo',
        gallery: 'Gallery',
        journal: 'Journal',
        docs: 'Docs',
        contactUs: 'Contact Us',
        sourceCode: 'Source Code',
        followTwitter: 'Follow Us on Twitter',
        followInstagram: 'Follow Us on Instagram',
        joinDiscord: 'Join Lopaka Hub on Discord',
        signIn: 'Sign In',
        projects: 'Projects',
    },
    legal: {
        cookies: 'We use cookies. {terms}, {privacy}',
        agreement: "By continuing, you agree to Lopaka's {terms} and {privacy}",
        terms: 'Terms of Service',
        privacy: 'Privacy Policy',
    },
    // Title and description per public page, read by the middleware and the
    // client router (src/core/route-metadata.ts).
    meta: {
        home: {
            title: 'Graphics editor and image converter for Arduino, ESP32, Adafruit_GFX, u8g2, TFT_eSPI',
            description:
                'UI graphics editor for embedded screens with pixel perfect code builder and export. Best for Arduino, STM32, ESP32. Converts images to uint32 and uint8 variables for Adafruit GFX, u8g2 and TFT_eSPI',
        },
        gallery: {
            title: 'Gallery of embedded display UI projects — Lopaka',
            description:
                'Browse community UI designs for OLED, TFT, and e-paper displays. Open any project in the Lopaka editor and generate Arduino, ESP32, or STM32 code from it.',
        },
        price: {
            title: 'Pricing and plans — Lopaka',
            description:
                'Compare free Lopaka access with Plus Monthly and Plus Lifetime. Unlock custom fonts, icon packs, animations, private projects, and unlimited screens.',
        },
        // The SoftwareApplication JSON-LD description in index.html.
        app: {
            description:
                'Browser-based graphics editor and image converter for Arduino, ESP32, and STM32 embedded screens. Supports Adafruit_GFX, u8g2, and TFT_eSPI libraries.',
        },
        // Titles of app pages, which have no language versions: the server serves
        // English and the client switches to the user's language after mount.
        appPages: {
            projects: 'Your projects — Lopaka',
            assets: 'Your assets — Lopaka',
            login: 'Sign in — Lopaka',
            loginExpired: 'Sign-in link expired — Lopaka',
            upgrade: 'Upgrade your plan — Lopaka',
            success: 'Payment received — Lopaka',
        },
    },
    home: {
        // The hero styles part of each line with an accent colour, so those
        // lines are stored as their parts; see src/components/main/main-copy.ts.
        hero: {
            headingLead: 'GUI design toolkit for',
            headingAccent: 'embedded displays',
            taglineLead: 'Ship graphics in minutes,',
            taglineAccent: 'not days',
            taglineTail: 'with visual editor + code generator for MCUs',
            getStarted: 'Get Started',
        },
        quickStart: {
            heading: 'Get your first screen working',
            intro: 'Start with a real display layout, tweak it visually, and export code you can drop into your project.',
            steps: {
                pick: 'Pick a display and GFX library',
                edit: 'Change text, icons, or layout',
                export: 'Export code and run it on hardware',
            },
            getStarted: 'Get started',
            browseTemplates: 'Browse templates',
            playVideo: 'Play the Lopaka quick start video',
            videoTitle: 'Lopaka quick start video',
            watchVideo: 'Watch the video to see how it works',
        },
        features: {
            heading: 'Embedded GUI made easy',
            tagline: 'A complete toolset for creating graphics that run on microcontrollers.',
            shipFaster: {
                title: 'Ship hardware faster',
                text: 'Automatically generate production-ready code for any MCU without manual coding. Focus on design, not pixel hunting.',
            },
            confidence: {
                title: 'Design with confidence',
                text: 'Optimized graphics that look crisp and clear on low-resolution hardware. What you see is exactly what appears on your device.',
                imageAlt: 'Pixel-perfect display preview',
            },
            style: {
                title: 'Build with style',
                text: 'Hundreds of optimized icons to guide your users. Handcrafted for small displays.',
                imageAlt: 'Icon library for embedded displays',
            },
            readability: {
                title: 'Enhance readability',
                text: 'Access a library of pixel-perfect fonts designed for clarity on embedded displays. Import TTF, OTF, BDF, and GFX fonts.',
            },
            platforms: {
                title: 'Use any platform',
                text: '{platforms} and more. One tool for all your embedded projects.',
            },
            projects: {
                title: 'Manage complex projects',
                text: 'Multi-layer screens and multiple displays in one project. Organize your UI with ease.',
                imageAlt: 'Multi-layer project management',
            },
            workflow: {
                title: 'Integrate with your workflow',
                text: 'Export assets in the exact format your project requires, from C++ to raw bitmaps. PNG, BMP, XBMP arrays ready for embedded use.',
            },
            brand: {
                title: 'Maintain brand consistency',
                text: 'Upload your own brand typography and custom assets.',
                result: 'See them rendered perfectly on your device.',
            },
        },
        templates: {
            heading: 'Hundreds of templates',
            tagline: 'Built by the community. Handpicked by our team.',
            exploreMore: 'Explore more',
        },
        plansHeading: 'Start building today',
        community: {
            contribute: {
                title: 'Contribute',
                text: 'Help us build the future of embedded UI. Open source and community driven.',
                action: 'View on GitHub',
            },
            discord: {
                title: 'Join Discord Hub',
                text: 'Connect with other developers, share your projects, and get help.',
                action: 'Join Server',
            },
            follow: {
                title: 'Follow Us',
                text: 'Stay updated with the latest news, features, and community showcases.',
            },
        },
        rating: {
            label: 'User rating',
            stars: '{rating} out of 5 stars',
            summary: 'Average rating based on {count} user survey responses',
        },
    },
    // Instagram comments on the landing and pricing pages; the comments stay as written.
    socialProof: {
        heading: 'The comments say enough',
        label: 'Instagram comments',
        avatarAlt: '{username} avatar',
    },
    // Signed-out visitors on phones, offered a link to continue on a desktop.
    mobileIntro: {
        heading: 'Continue on desktop',
        text: 'Lopaka is designed as a desktop application to provide the best experience.',
        close: 'Close desktop setup prompt',
        send: 'Send me a link',
        sending: 'Sending link',
        sent: 'We sent a desktop setup link to your email.',
        failed: 'Could not send the setup link. Please try again.',
        watchGuide: 'Watch the quick start guide',
    },
    gallery: {
        tabs: {
            highlights: {title: 'Highlights', description: 'Handpicked by Lopaka team'},
            trending: {title: 'Trending', description: 'Most starred and forked'},
            favourite: {title: 'Favourites', description: 'Your stars'},
            all: {title: 'Everything', description: 'All public projects'},
        },
        clearFilters: 'Clear filters',
        empty: 'No gallery projects found.',
        showMore: 'Show more',
        project: {
            screens: 'Screens: {count}',
            starThis: 'Star this',
            stars: 'Stars: {count}',
            forks: 'Forks: {count}',
        },
    },
    // Filters shared by the gallery and the projects list.
    filters: {
        width: 'Width',
        height: 'Height',
        all: 'All',
        allPlatforms: 'All platforms',
        search: 'Search',
        clearSearch: 'Clear project name filter',
    },
    pricing: {
        features: {
            codeGeneration: 'Code Generation',
            imageProcessing: 'Image Processing',
            communityFonts: 'Community Fonts',
            communityIcons: 'Community Icons',
            screens: '{count} screens',
            assetsStorage: '{count} Assets storage',
            customFonts: 'Custom Fonts',
            extraIcons: 'Extra 1500+ Icons',
            publicProjects: '{count} Public Projects',
            screensPerProject: '{count} Screens per project',
            animations: 'Animations',
            exportOptions: 'Export options',
            everythingInBasic: 'Everything in basic, and:',
            unlimitedScreens: 'Unlimited Screens',
            unlimitedAssets: 'Unlimited Assets',
            privacySettings: 'Privacy settings',
            presentationMode: 'Presentation mode',
            teamCollaboration: 'Team Collaboration',
            customPlatformSupport: 'Custom Platform Support',
            apiAccess: 'API Access & Integrations',
            customFeatures: 'Custom features',
            userManagement: 'SSO & User Management',
            onPremise: 'On-premise deployment',
            whiteLabel: 'White-label Options',
            volumeLicensing: 'Volume Licensing',
            dailyBackups: 'Daily Backups',
            onboarding: 'Training & Onboarding',
            dedicatedSupport: 'Dedicated Support',
        },
        upgradeHeading: 'Upgrade your plan to unlock {plan} features:',
        teamsDescription: 'Tailored solutions for hardware companies, IoT firms, and development teams',
        card: {
            free: 'FREE',
            save: 'Save {amount}',
            comparedToMonthly: 'Compared to monthly',
            perYear: '/ year',
            perMonth: '/ month',
            monthlyEquivalent: '~ {price}/month',
            sevenDayPass: '7-Day Pass',
            lifetimeAccess: 'Lifetime access',
            oneTimePayment: 'One-time payment',
            everythingInPlus: 'Everything in Plus and:',
            currentPlan: 'Current plan',
            signUp: 'Sign Up',
            requestQuote: 'Request a quote',
        },
        actions: {
            buyNow: 'Buy now',
            getWeekPass: 'Get 7-day access',
            requestLifetime: 'Request Lifetime',
            getLifetime: 'Get lifetime license',
            changeToAnnual: 'Change to Annual',
            changeToMonthly: 'Change to Monthly',
            changeToPlus: 'Change to Plus',
            upgradeToPlus: 'Upgrade to Plus',
        },
        referral: {
            extraDays: '+ You get 7 extra days',
            discount: '+ You get a 10% discount',
        },
        education: {
            heading: 'Lopaka for Classrooms',
            question: 'Are you a teacher looking to use Lopaka Plus for educational purposes?',
            offer: 'We provide {access} to educators who want to use Lopaka in their classrooms.',
            access: 'free Plus access for 1 year',
            contact: 'Contact us',
        },
    },
    // Sign-in, account creation, and password reset (/login, /login-expired).
    auth: {
        signInTab: 'Sign In',
        registerTab: 'Create New Account',
        or: 'OR',
        continueWithGithub: 'Continue with GitHub',
        continueWithGoogle: 'Continue with Google',
        signOut: 'Sign Out',
        email: 'Email',
        password: 'Password',
        passwordPlaceholder: 'Enter your password',
        newPassword: 'New password',
        newPasswordPlaceholder: 'Create a new password',
        confirmPassword: 'Confirm password',
        confirmPasswordPlaceholder: 'Repeat your new password',
        forgotPassword: 'Forgot password?',
        backToSignIn: 'Back to sign in',
        resetSent: 'Open the email we sent to {email} and follow the recovery link to choose a new password.',
        registerSent: 'We sent a confirmation link to {email}',
        registerSentNext: 'Open the email to verify your account and finish signing in.',
        modes: {
            signIn: {title: 'Sign in to your account', submit: 'Sign In'},
            register: {title: 'Create your account', submit: 'Create an Account'},
            forgotPassword: {title: 'Reset your password', submit: 'Request password reset'},
            resetPassword: {title: 'Choose a new password', submit: 'Update password'},
        },
        messages: {
            signingIn: 'You are being signed in.',
            resetLinkSent: 'We sent a password reset link to {email}.',
            passwordUpdated: 'Your password has been updated.',
        },
        validation: {
            signInMissing: 'Enter your email and password.',
            registerMissing: 'Enter your email and create a password.',
            accountExists: 'An account with this email already exists. Please sign in instead.',
            resetEmailMissing: 'Enter your email to reset the password.',
            newPasswordMissing: 'Enter a new password.',
            passwordsDiffer: 'Passwords must match.',
        },
        // Supabase Auth error codes; any other error shows the server's English text.
        errors: {
            invalid_credentials: 'The email or password is incorrect.',
            email_not_confirmed: 'Confirm your email address first: open the link we sent you.',
            user_already_exists: 'An account with this email already exists. Please sign in instead.',
            email_exists: 'An account with this email already exists. Please sign in instead.',
            weak_password: 'This password is too weak. Choose a longer password.',
            same_password: 'The new password must be different from the current one.',
            email_address_invalid: 'Enter a valid email address.',
            over_email_send_rate_limit: 'Too many emails sent. Please wait a few minutes and try again.',
            over_request_rate_limit: 'Too many attempts. Please wait a few minutes and try again.',
        },
        expired: {
            title: 'Reset link expired',
            text: 'This password reset link is invalid or has expired. Request a new reset email and try again.',
            backToProjects: 'Back to projects list',
        },
        emailPlaceholder: 'you{at}example.com',
    },
    notFound: {
        title: 'Page Not Found',
        text: 'The page you are looking for could not be found. It might have been deleted, made private, or you may have entered the wrong URL.',
        toGallery: 'Go to Gallery',
        toProjects: 'My Projects',
    },
    appUpdate: {
        available: 'New Lopaka version is available!',
        update: 'Update',
        loadFailed: 'Failed to load page',
        loadFailedText:
            'The page could not be loaded. This can happen due to a network issue, an ad blocker, or a recent app update.',
        reload: 'Reload page',
    },
    // Prose of the crawlable block the middleware puts into the app shell
    // (functions/_shared/page-content.ts). `…Link` keys are link texts.
    pageShell: {
        home: {
            exportTargets: 'Design a screen in the browser, then export ready-to-compile source for {libraries}.',
            imageImport:
                'Import PNG, BMP, GIF, or video and Lopaka converts each frame to the format your display needs, from 1-bit XBMP bitmaps for monochrome OLEDs to RGB565 arrays for color TFTs.',
            gallery: 'Browse the {galleryLink}, open any design in the editor, and generate code from it.',
            galleryLink: 'project gallery',
            more: 'Read the {docsLink} or compare {pricingLink}.',
            docsLink: 'documentation',
            pricingLink: 'plans and pricing',
        },
        gallery: {
            heading: 'Gallery of embedded display UI projects',
            intro: 'Community UI designs for OLED, TFT, and e-paper displays. Open any project in the Lopaka editor and generate Arduino, ESP32, or STM32 code from it.',
            more: 'Start your own design in the {editorLink} or read the {docsLink}.',
            editorLink: 'Lopaka editor',
            docsLink: 'documentation',
        },
        price: {
            heading: 'Pricing and plans',
            intro: 'Compare the {plans} plans for the Lopaka embedded GUI editor, billed monthly or annually.',
            plans: {
                basic: 'Free to use.',
                hobby: 'For regular personal projects.',
                plus: 'For unlimited work.',
                teams: 'Tailored solutions for hardware companies, IoT firms, and development teams.',
            },
            includes: 'Includes {features}.',
            teachers: 'Teachers can request free Plus access for one year. Plan details are in the {docsLink}.',
            docsLink: 'plans documentation',
        },
    },
    common: {
        cancel: 'Cancel',
        save: 'Save',
        delete: 'Delete',
        close: 'Close',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        finish: 'Finish',
        done: 'Done',
        apply: 'Apply',
        confirm: 'Confirm',
        areYouSure: 'Are you sure?',
        edit: 'Edit',
        rename: 'Rename',
        duplicate: 'Duplicate',
        remove: 'Remove',
        add: 'Add',
        copy: 'Copy',
        copied: 'Copied',
        download: 'Download',
        upload: 'Upload',
        search: 'Search',
        reset: 'Reset',
        retry: 'Try again',
        loading: 'Loading…',
        saving: 'Saving…',
        upgrade: 'Upgrade',
        upgradeNow: 'Upgrade Now',
        public: 'Public',
        private: 'Private',
        configure: 'Configure',
        projectSettings: 'Project settings',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
        none: 'None',
        yes: 'Yes',
        no: 'No',
        learnMore: 'Learn more',
        unknownError: 'Something went wrong. Please try again.',
    },
    errors: {
        unauthenticated: 'Sign in to manage your projects.',
        ownership_denied: 'This project or screen belongs to another account.',
        not_found: 'The project or screen was not found. It may have been deleted.',
        limit_reached: "You've reached your plan's limit. Upgrade to add more.",
        invalid_input: 'This action is not possible here.',
        read_failed: 'Could not load your data. Check your connection and try again.',
        write_failed: 'Could not save your changes. Check your connection and try again.',
        screenLimit: "You've reached your plan's screen limit. Upgrade to add more screens.",
        animationTooLarge: 'The animation exceeds 5 MiB. Reduce the frame count or canvas dimensions before saving.',
        animationUploadUnknown:
            'We could not confirm that the animation was saved. Check your connection and save again.',
        animationLastFrame: 'An animation needs at least one frame.',
        assetLimit: 'Asset upload limit reached. Upgrade your plan to upload more.',
        signInToImportImages: 'Sign in to import images.',
        signInToSaveAnimations: 'Sign in to save animations.',
        screenCodeLoading: 'Wait for the screen code to load.',
        codeEditorClosed: 'The code editor is closed.',
        readonly: 'This screen is read-only.',
        stale_context: 'The screen changed before the action finished. Try again.',
        unsupported_for_platform: 'The current platform does not support this.',
        upgrade_required: 'Upgrade your plan to use this.',
        importOneVideo: 'Choose one video file at a time.',
        importVideoUnavailable: 'Video animation import is unavailable in this editor.',
        importVideoUnreadable: 'The selected video container could not be read.',
        importVideoNoTrack: 'The selected file does not contain a video track.',
        importVideoCodec: 'This browser cannot decode the video codec used by the selected file.',
        importVideoInvalid: 'The selected video has invalid duration or dimensions.',
        importVideoNoPreview: 'No video preview frames could be decoded.',
        importGifNoFrames: 'Select at least one GIF frame to import.',
        importGifFrameSize: 'Every GIF frame must have the same processed dimensions.',
        importGifTooLarge: 'The processed GIF exceeds the animation size or frame limit.',
    },
    projects: {
        heading: 'My Projects',
        newProject: 'New Project',
        createHint: 'Create a new project',
        upgradeHint: 'Upgrade to add more projects',
        projectLimit: {
            title: 'You’ve reached the Projects limit of your current plan.',
            text: 'Upgrade now to unlock more projects and advanced features.',
        },
        screenLimit: {
            title: 'You’ve reached the Screens limit of your current plan.',
            text: 'Upgrade now to unlock more screens and advanced features.',
        },
        noFilterResults: 'No projects found for selected filters.',
        resetFilters: 'Reset filters',
        empty: 'Start with creating a {newProject} or explore templates in {gallery}',
        emptyNewProject: 'New project',
        emptyGallery: 'Gallery',
        community: {
            eyebrow: 'Community',
            heading: 'Projects Gallery',
            exploreMore: 'Explore more',
        },
        favouritesOnly: 'Show favourites only',
        card: {
            copy: 'Copy this project',
            screens: 'Screens: {count}',
        },
        title: {
            publicHint: 'This project is visible to all users in Gallery',
            publicProject: 'Public project',
            deleteHint: 'Permanently delete project',
        },
        examples: {
            eyebrow: 'Quickstart',
            heading: 'Example Projects',
            open: 'Open in editor',
            videoHeading: 'Video Tutorial',
            videoTitle: 'YouTube video player',
            colorful: 'colorful',
            monochrome: 'monochrome',
            speedometer: 'Vehicle dashboard with speed, RPM, odometer, voltage, fuel, and ignition status',
            tirePressure: 'Showing live readings for all four wheels on a top-down car diagram',
        },
        wizard: {
            editTitle: 'Edit Project',
            createTitle: 'Create New Project',
            platform: 'Platform',
            title: 'Title',
            titlePlaceholder: 'Enter project title',
            screenSize: 'Screen Size',
            swap: 'Swap dimensions',
            selectSize: 'Select screen size',
            custom: 'Custom',
            rounded: 'Rounded',
            roundedHint: 'Round display: width = height, circular mask on canvas',
            background: 'Background Color',
            backgroundHex: 'Background color hex',
            publish: 'Publish to Gallery',
            publicHint: 'Public projects are visible to all users',
            upgradeHint: 'Get unlimited screens and privacy settings',
            deleteHint: 'Remove this Project with its Screens permanently. Forever. Be careful.',
            createPublic: 'Create Public Project',
            createFailed: 'Project could not be created.',
            saveFailed: 'Project could not be saved.',
            deleteFailed: 'Project could not be deleted.',
            defaultTitle: 'Project {number}',
        },
        copyFailed: 'Project could not be copied.',
    },
    animationEditor: {
        topBar: {
            frameCounter: 'Frame {current} / {total}',
            cancelHint: 'All unsaved animation changes will be lost.',
        },
        timeline: {
            deleteFrame: 'Delete selected frame',
            firstFrame: 'Jump to first frame',
            previousFrame: 'Previous frame',
            play: 'Play animation',
            pause: 'Pause animation',
            nextFrame: 'Next frame',
            lastFrame: 'Jump to last frame',
            duplicateFrame: 'Duplicate frame',
            addFrame: 'Add frame',
            animationColor: 'Animation color',
            totalLength: 'Total length',
            durationSeconds: '{seconds} s',
            fpsHint: 'Frames per second from 1 to 60',
            fps: 'Frames per second',
            fpsValue: '{fps} FPS',
            resizeCanvas: 'Resize canvas',
            estimatedSize: 'Estimated size',
            onionSkinHint: 'Show previous frame',
            onionSkin: 'Onion skin',
            onion: 'Onion',
            frame: 'Frame {number}',
            dropCopy: '+ Copy',
            dropMove: 'Move',
        },
        resizeDialog: {
            title: 'Resize animation canvas',
            description: 'Pixels stay anchored to the top-left and are not scaled.',
            width: 'Width',
            height: 'Height',
            resize: 'Resize',
            close: 'Close resize dialog',
        },
        errors: {
            previewFailed: 'Animation preview failed.',
            frameLoadFailed: 'Frame could not be loaded.',
            operationFailed: 'Animation operation failed.',
            playbackFailed: 'Animation playback could not start.',
            flattenFailed: 'The current frame could not be flattened.',
            imageAddFailed: 'Image could not be added.',
            animationLoadFailed: 'Animation could not be loaded.',
            animationAddFailed: 'Animation could not be added.',
            assetUnavailable: 'Animation asset is unavailable. A blank editable animation was opened instead.',
        },
    },
    inspector: {
        selectedLayers: 'Selected layers ({count})',
        labels: {
            font: 'Font',
            fontSize: 'Size',
            inverted: 'Inverted (XOR)',
            fill: 'Filled',
            color: 'Fill color',
            overlay: 'Overlay',
            alphaChannel: 'Transparency',
            radius: 'R',
            rx: 'RX',
            ry: 'RY',
            w: 'W',
            h: 'H',
            backgroundColor: 'Back',
            borderColor: 'Border',
            borderWidth: 'B',
            checked: 'Checked',
            rotation: 'Rotate',
            startAngle: 'Start',
            endAngle: 'End',
            value: 'Value',
            width: 'Width',
            smooth: 'Smooth',
        },
        labelHints: {
            radius: 'Radius',
            rx: 'Horizontal radius',
            ry: 'Vertical radius',
            w: 'Width',
            h: 'Height',
            backgroundColor: 'Background color',
            borderColor: 'Border color',
            borderWidth: 'Border width',
            rotation: 'Rotation',
            startAngle: 'Start angle',
            endAngle: 'End angle',
        },
        tooltips: {
            alphaChannel: 'Keep transparent pixels see-through. Off: they are filled with the screen background',
            overlay: 'Dim the layer and skip it from code generation',
        },
        textPlaceholder: 'Enter text...',
        hexColor: '{label} hex color',
        addVariable: 'Add variable',
        removeVariable: 'Remove variable',
        rotateText: 'Rotate text {degrees}°',
        mergeToBitmap: 'Merge to Bitmap',
        mergeHint: 'Merge layers into a single bitmap',
        editAnimationHint: 'Edit animation frames',
        mergeFailed: 'The layers could not be merged.',
        mergeTooLarge: 'The merged image is too large. Move the selected layers closer together.',
        mergeNothingSelected: 'Select layers to merge.',
        fontLoadFailed: 'Unable to load the font. Check that it follows the GFX or BDF format.',
        align: {
            left: 'Align left',
            centerH: 'Align horizontal centers',
            right: 'Align right',
            top: 'Align top',
            centerV: 'Align vertical centers',
            bottom: 'Align bottom',
        },
        imageActions: {
            flipH: 'Flip horizontally',
            flipV: 'Flip vertically',
            rotate: 'Rotate clockwise',
            invert: 'Invert colors',
            trimPadding: 'Remove blank padding',
            download: 'Download image',
            saveAsset: 'Add to Project assets',
            saveAssetLabel: 'Save Asset',
            imageSaved: 'Image is saved to project assets.',
        },
        colorInput: {
            noBackground: 'No background',
            clearBackground: 'Clear background',
        },
        colors: {
            red: 'Red',
            pink: 'Pink',
            purple: 'Purple',
            deepPurple: 'Deep Purple',
            indigo: 'Indigo',
            blue: 'Blue',
            lightBlue: 'Light Blue',
            cyan: 'Cyan',
            teal: 'Teal',
            green: 'Green',
            lightGreen: 'Light Green',
            lime: 'Lime',
            yellow: 'Yellow',
            amber: 'Amber',
            orange: 'Orange',
            deepOrange: 'Deep Orange',
            brown: 'Brown',
            blueGrey: 'Blue Grey',
        },
    },
    paint: {
        selectBrushColor: 'Select brush color {color}',
        paintColor: 'Paint color',
        brushSize: 'Brush size',
        mono: 'Mono',
        cropImage: 'Crop image',
        applyCropHint: 'Apply crop (Enter)',
        cancelCropHint: 'Cancel crop (ESC)',
        eraser: 'Eraser',
        fill: 'Fill',
        patternHint: 'Brush shape and pattern',
        sizeLimit:
            'This Paint layer reached its maximum size of {pixels} pixels. Start a new Paint layer to draw further away.',
        patterns: {
            full: 'Square',
            circle: 'Circle',
            dots: 'Dots',
            dots2: 'Dots (2px)',
            checkerboard: 'Checkerboard',
            checkerboardReverse: 'Checkerboard (reverse)',
            grid: 'Grid',
            horizontal1: 'Horizontal lines (1px)',
            horizontal2: 'Horizontal lines (2px)',
            vertical1: 'Vertical lines (1px)',
            vertical2: 'Vertical lines (2px)',
            diagonal2: 'Diagonal (2px)',
            diagonal2Reverse: 'Diagonal reverse (2px)',
            diagonal3: 'Diagonal (3px)',
            diagonal3Reverse: 'Diagonal reverse (3px)',
        },
    },
    fonts: {
        importFont: 'Import font...',
        deleteFont: 'Delete font',
        fontInUse: 'Cannot delete a font that is being used in the current project',
        readFailed: 'Could not read this font file.',
        wizard: {
            previewTitle: 'Font Preview: {name}',
            previewZoom: 'Preview Zoom',
            fontHeight: 'Font Height',
            previewText: 'Preview Text',
            import: 'Import',
        },
    },
    screens: {
        addScreen: 'Add Screen',
        upgradeToAdd: 'Upgrade to add more screens',
        fallbackTitle: 'Screen',
        creating: 'Creating screen',
        deleting: 'Deleting screen',
        hiddenInProjectSettings: 'Hidden in project settings',
        actions: {
            publish: 'Publish',
            makePublic: 'Make Public',
            makePrivate: 'Make Private',
            unlimitedHint: 'Upgrade to get unlimited screens',
            privacyHint: 'Upgrade for privacy',
        },
        carousel: {
            open: 'Open {project} in Canvas Flow',
            previewAlt: '{project} - Screen {number}',
            screenTitle: 'Screen {number}',
            previewUnavailable: 'Preview unavailable',
            emptyCanvas: 'Empty canvas',
            previous: 'Previous screen',
            next: 'Next screen',
        },
    },
    flow: {
        layers: 'Layers',
        screensLeft: '{count} free screen left. | {count} free screens left.',
        seePlans: 'See plans that offer more',
        canvasLabel: 'Project canvas',
        imageSaved: 'Image saved successfully',
        view: {
            fitAll: 'Fit all',
            fitSelection: 'Fit selection',
            hideInspector: 'Hide inspector',
            showInspector: 'Show inspector',
        },
        liveView: {
            connect: 'Live View',
            disconnect: 'Disconnect',
            hint: 'Preview your design in real-time on your Flipper via USB',
            noDevice: 'No Flipper device found. Please connect your Flipper via USB and try again.',
            disconnected: 'Flipper Live View was disconnected. Please reconnect your Flipper via USB and try again.',
        },
        code: {
            tabs: 'Screen code',
            copyHint: 'Copy source code',
            empty: 'Create a screen to generate code.',
            importHint: 'Import code - Experimental: Works best with code generated in Lopaka',
            loadFailed: 'The screen code could not be loaded.',
            generateFailed: 'Code could not be generated for this screen.',
            importFailed: 'The code could not be imported.',
            copyFailed: 'Could not copy code to the clipboard.',
        },
        animation: {
            title: 'Animation',
            discardChanges: 'Discard unsaved animation changes?',
        },
        errors: {
            openFailed: 'The project could not be opened.',
            screenAddFailed: 'The screen could not be added. Please try again.',
            screenUpdateFailed: 'The screen could not be updated.',
            imageLoadFailed: 'A project image could not be loaded.',
            assetsLoadFailed: 'Some project assets could not be loaded.',
            unsavedKeepOpen: "Your latest changes haven't been saved. Keep this tab open.",
            unsaved: "Your latest changes haven't been saved.",
            previewFailed: 'Screen preview could not be updated.',
            imageUploadFailed: 'Image {name} could not be uploaded.',
            imagesImportFailed: 'The images could not be imported.',
            assetAddFailed: 'The asset could not be added.',
            imageSaveFailed: 'The image could not be saved.',
            animationOpenFailed: 'The animation could not be opened.',
            animationSaveFailed: 'The animation could not be saved.',
            documentUpdateFailed: 'The change could not be applied to the canvas.',
        },
    },
    platforms: {
        picker: {
            placeholder: 'Select a platform',
            setup: 'Setup: {hint}',
            memory: 'Memory: {hint}',
            noDescription: 'No description available.',
            github: 'View on GitHub',
            mono: 'Mono',
            easy: 'Easy',
            hard: 'Hard',
        },
        details: {
            tftEspi: {
                description:
                    "One drawing API for the color SPI panels — ILI9341, ST7789, GC9A01 etc. The driver, pins and SPI speed live in the library's own User_Setup.h, so the library is configured before your sketch compiles, not from your code. M5GFX and LovyanGFX expose the same calls. No widgets and no automatic redraw: you draw when you decide to draw. Code exports as Arduino C++ or ESP-IDF C++.",
                sizeHint: 'Needs ESP32, ESP8266, RP2040 or STM32',
                setupHint: 'Driver and pins set in User_Setup.h',
            },
            u8g2: {
                description:
                    'The default choice for monochrome OLED and LCD panels such as SSD1306, SH1106 and ST7565, where every pixel is simply on or off. Its font list runs to hundreds of BDF variants, which is the usual reason to pick it, and font size comes from the font file rather than a scale factor. Code exports as Arduino C++ or ESP-IDF C.',
                sizeHint: 'Page buffering runs on an Arduino Uno',
                setupHint: 'One constructor line, no config file',
                monoHint: 'Monochrome only: one bit per pixel, no color',
            },
            adafruitGfx: {
                description:
                    'The shared drawing layer under most Adafruit display drivers, which is why examples and tutorials for it are everywhere. You pair it with the driver for your exact panel, such as Adafruit_ST7789 or Adafruit_ILI9341, and that driver owns the pins and initialization. Colors are packed RGB565 and text comes from the GFX font headers. Drawing goes straight to the panel with no framebuffer, so even 8-bit boards can drive a color screen. Code exports as Arduino C++ or ESP-IDF C++.',
                sizeHint: 'No framebuffer; fits 8-bit boards',
            },
            adafruitGfxMono: {
                description:
                    'The same GFX calls and fonts against a one-bit screen, driven by Adafruit_SSD1306 or Adafruit_SH110X. The driver keeps the whole screen in a RAM buffer and pushes it on display(), which costs roughly 1 KB on a 128x64 panel. For a far larger font collection on the same hardware, use U8g2 instead. Code exports as Arduino C++ or ESP-IDF C++.',
                sizeHint: 'Fits 8-bit boards like Arduino Uno',
                setupHint: 'Install driver, run its example',
                monoHint: 'Monochrome only: one bit per pixel, no color',
            },
            lvgl: {
                description:
                    'A GUI toolkit rather than a drawing library: buttons, sliders, switches, checkboxes and text areas are real objects with their own styles and event handling. Porting it means an lv_conf.h plus a flush callback that hands rendered pixels to your display driver, so expect setup work before the first pixel appears. It wants a 32-bit MCU with spare RAM.',
                sizeHint: 'Wants a 32-bit board with spare RAM',
                setupHint: 'Needs lv_conf.h and a flush callback',
            },
            arduinoGfx: {
                description:
                    'A newer take on the GFX API where the display object is assembled in the sketch from a data bus plus a panel class, instead of editing a library config file. Beyond the shared primitives it adds its own bitmap entry points, including a big-endian variant for byte streams that arrive high byte first. Aimed at faster 32-bit boards, including parallel and RGB panels on ESP32-S3. Code exports as Arduino C++ or ESP-IDF C++.',
                sizeHint: 'Tuned for 32-bit boards like ESP32',
            },
            esphome: {
                description:
                    'Firmware for ESP32 and ESP8266 described in a single YAML file: ESPHome builds and flashes it for you, and the device appears in Home Assistant. Drawing lives inside the display component lambda, while fonts and images are separate top-level YAML keys rather than part of that lambda. Fonts can be pulled straight from Google Fonts by URL. Images export as 1-bit XBMP only.',
                sizeHint: 'Whole firmware; needs ESP32 or ESP8266',
                setupHint: 'YAML only; ESPHome compiles and flashes',
            },
            micropython: {
                description:
                    'Python running on the board itself: copy a .py file across and it executes, with no toolchain and no compile step. Drawing uses the built-in framebuf module through the PerfecXX MicroPython-SSD1306 driver, the one file to copy onto RP2040, ESP32 and similar boards. It is one-bit only, with a single fixed 8x8 font and no rounded corners, which suits readouts and simple menus more than detailed layouts. Triangles here are outline-only.',
                sizeHint: 'Needs a MicroPython board, RP2040 or ESP32',
                setupHint: 'Copy a .py file to the board',
                monoHint: 'Monochrome only: one bit per pixel, no color',
            },
            circuitpython: {
                description:
                    "Adafruit's Python variant: the board mounts as a USB drive and your code reruns the moment you save the file. Graphics go through the displayio and vectorio stack, which composes groups of display objects and keeps refreshing them, instead of you pushing pixels each frame. Colors are 24-bit hex, text uses the bundled Terminus BDF font, and images ship as raw byte buffers. You need a board with a CircuitPython build that supports your display.",
                sizeHint: 'Needs a CircuitPython board with displayio',
                setupHint: 'Board mounts as a USB drive',
            },
            flipper: {
                description:
                    'Draws on the Flipper Zero and Flipper One screen through the firmware canvas API, so the output belongs inside a Flipper application built with the firmware app SDK. Four fixed fonts only.',
                monoHint: 'Monochrome only: one bit per pixel, no color',
                setupHint: 'Built through the Flipper app SDK',
            },
            gxepd2: {
                description:
                    'For e-paper panels. It sits on top of Adafruit GFX, so drawing calls and fonts are the familiar ones, but you must name your exact panel class from a long list where the model number on the ribbon cable matters. Color is a fixed set of nine constants such as GxEPD_BLACK and GxEPD_RED, and anything else maps to black.',
                sizeHint: 'Panel buffers are large; paging helps',
                setupHint: 'Pick the exact panel class',
            },
            freestyle: {
                description:
                    'A plain canvas with no target library: full RGB color, every font Lopaka has, and no generated code at all. Use it to sketch a layout or mock up a screen before committing to hardware, or simply to export an image.',
            },
        },
    },
    account: {
        menu: {
            assets: 'Assets',
            plusAccessDaysLeft: 'Plus Access: {count} day left | Plus Access: {count} days left',
            subscriptionDaysLeft: 'Subscription: {count} day left | Subscription: {count} days left',
        },
        referral: {
            invite: 'Invite a Friend',
            getFreePlus: 'Get a free Plus',
        },
        redirecting: 'Redirecting…',
        cancelFlow: {
            adBlocked:
                'Subscription cancellation is blocked by your ad blocker. Please disable it for lopaka.app and try again.',
            paddleUnavailable: 'Unable to open Paddle cancellation flow. Please try again or contact support.',
            unavailable: 'Unable to open subscription cancellation. Please contact support.',
        },
        success: {
            heading: 'Processing Your Payment',
            wait: 'Please wait while we verify your subscription.',
            moments: 'This usually takes a few moments.',
            checking: 'Checking subscription status...',
            progress: '{percent} complete',
            help: 'Contact {email} if you continue to have issues',
        },
    },
    subscription: {
        manage: 'Manage Subscription',
        cancel: 'Cancel Subscription',
        cycles: {
            weekly: 'Weekly',
            monthly: 'Monthly',
            yearly: 'Yearly',
            lifetime: 'Lifetime',
        },
        upgradeModal: {
            title: 'Upgrade Subscription',
            intro: 'You are about to request an upgrade from {current} to {target}.',
            currentPlan: 'Current plan:',
            requestedPlan: 'Requested plan:',
            requestedCycle: 'Requested billing cycle:',
            renewalDate: 'Current subscription renewal date:',
            nextRenewal: 'Next renewal date:',
            newPrice: 'New price after upgrade:',
            manualNote:
                'Upgrades are currently processed manually. After confirmation we will process it as soon as possible. In case of any issues, please contact {email}.',
            sent: 'Your upgrade request was sent. We will contact you shortly.',
            failed: 'Failed to send upgrade request. Please try again.',
            confirm: 'Confirm Upgrade',
            unknownPlan: 'Unknown',
            dateUnavailable: 'Date is not available',
        },
    },
    upgrade: {
        eyebrow: 'Subscription change',
        headingYearly: 'Change to Yearly {plan}',
        headingMonthly: 'Change to Monthly {plan}',
        intro: "Review Paddle's billing preview before confirming your subscription change.",
        loading: 'Preparing your subscription change preview...',
        renewalYearly: 'Yearly renewal',
        renewalMonthly: 'Monthly renewal',
        credit: 'Credit',
        payAtRenewal: 'Pay at renewal',
        payToday: 'Pay today',
        offerApplied: 'Offer {code} will be applied to this change.',
        reactivation:
            'Your subscription is canceled, but paid access remains active until {date}. Confirming this change will reactivate billing and apply the prorated upgrade immediately.',
        reactivationPeriodEnd:
            'Your subscription is canceled, but paid access remains active until the end of your paid period. Confirming this change will reactivate billing and apply the prorated upgrade immediately.',
        scheduledNote:
            'This downgrade is scheduled for your next billing cycle, so your current access stays active until renewal.',
        prorationNote:
            'Paddle calculates the final prorated amount using your current billing period, taxes, currency, and any eligible offer.',
        confirm: 'Confirm change',
        changed: 'Your subscription was changed.',
        scheduled: 'Your subscription change was scheduled for the next billing cycle.',
        backToProjects: 'Back to projects',
        contactSupport: 'Contact support',
        errorFallback: 'Unable to change your subscription. Please contact {email}',
        previewFailed: 'Unable to prepare subscription change preview.',
        changeFailed: 'Unable to change your subscription.',
        cannotChangeAutomatically: 'This subscription cannot be changed automatically.',
        continueWithBasic: 'Continue with Basic plan →',
        eligibility: {
            noActiveAnnual: 'No active subscription is available for annual upgrade.',
            noActive: 'No active subscription is available for this change.',
            pastDueAnnual: 'Please update your payment method before switching to annual billing.',
            pastDue: 'Please update your payment method before changing your subscription.',
            legacyAnnual: 'This subscription cannot be upgraded automatically. Please contact support.',
            manualSupport: 'This subscription cannot be changed automatically. Please contact support.',
            planMismatch: 'This upgrade link does not match your current subscription plan.',
            alreadyAnnual: "You're already on annual billing.",
            unknownCycle: 'We could not confirm your current billing cycle. Please contact support.',
            eligibleAnnual: 'Your subscription can be switched to annual billing.',
            weeklyManual: 'Weekly access changes are handled manually. Please contact support.',
            lifetimeManual: 'Lifetime access does not use recurring billing. Please contact support.',
            yearlyToMonthly: 'Switching from yearly to monthly billing is handled manually. Please contact support.',
            alreadyCurrent: 'You already have this subscription.',
            downgradeManual: 'Downgrades are handled manually so your current access stays active until renewal.',
            eligible: 'Your subscription can be changed immediately.',
        },
    },
    cancellation: {
        intro: 'Please answer a short survey',
        redirecting: 'Redirecting you to the subscription management portal...',
        step: 'Step {current} of {total}',
        change: 'Change',
        otherDetails: 'Could you please share what exactly?',
        otherPlaceholder: 'Please tell us more...',
        commentsTitle: 'Anything else we should know?',
        commentsHint: 'Your feedback helps us make Lopaka better for everyone.',
        commentsPlaceholder: 'Details about your experience, features you missed, or anything else...',
        questions: {
            mainReason: 'What is the main reason for cancellation?',
            contributingFactors: 'Did anything else contribute?',
            experience: 'Rate your overall experience?',
            future: 'What could convince you to stay?',
        },
        options: {
            notEnoughValue: "I don't see enough value in the paid plan",
            freeTierEnough: 'The free tier is enough for me',
            switchedTool: 'Switched to another tool',
            other: 'Other',
            nothingElse: "No, that's all",
            lowerPrice: 'Lower price',
            betterOnboarding: 'Better onboarding',
            moreExport: 'More export options',
            alreadyDecided: "Nothing, I've already decided",
        },
    },
    referrals: {
        closeDialog: 'Close invitation dialog',
        invite: {
            heading: 'Invite a friend',
            text: 'Earn a free week of Plus when your friend makes their first purchase.',
            ticketAlt: 'Seven days of Lopaka Plus for every paying friend',
            friendBenefit: 'Your friend gets {extraDays} on their first Plus week, {lineBreak}or {discount}.',
            extraDays: '7 extra days',
            discount: '10% off on Lifetime',
            linkLabel: 'Your invitation link',
            copyLink: 'Copy invitation link',
            linkCopied: 'Invitation link copied',
            paidReferrals: 'Paid referrals',
            bonusDays: 'Bonus days earned',
        },
        share: {
            heading: 'Share your work',
            text: 'Earn a free week of Plus for every approved post about Lopaka.',
            ticketAlt: 'Seven days of Lopaka Plus — One week per posting',
            ideas: 'Show your project, write a tutorial or article, or share a post or video. Mention {brand} or link to {site} where possible.',
            linkLabel: 'Link to your post',
            submit: 'Submit for review',
            submitted: "Submitted! We'll email you once it's approved.",
            alreadySubmitted: 'This post was already submitted.',
            failed: 'Could not submit your post. Please try again.',
            reviewNote:
                "We review every link manually and email you once it's approved. Have more to share? Send a separate link for each post.",
        },
    },
    editor: {
        screenNotFound: '404 Screen Not Found',
        uploadingImages: 'Uploading images. Please wait...',
        animationTitle: 'Animation',
        grid: 'Grid',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        imageTooLarge: 'The image is {width}×{height} pixels; images can have at most {max} pixels.',
        layerDataMissing: 'Image layer data is missing. Skipping render.',
        loadingProject: 'Loading project',
        flipper: {
            liveView: 'Live View',
            disconnect: 'Disconnect',
            liveViewHint: 'Preview your design in real-time on your Flipper via USB',
            notFound: 'No Flipper device found. Please connect your Flipper via USB and try again.',
        },
        animation: {
            preparing: 'Preparing animation frames',
            preparingProgress: 'Preparing animation frames ({completed}/{total})',
            discardChanges: 'Discard unsaved animation changes?',
            editingUnavailable: 'Frame animation editing is available only in an editable saved project.',
            prepareFailed: 'Animation could not be prepared.',
            saveFailed: 'Animation changes could not be saved.',
            checkingSave: 'Checking the previous Save result before closing...',
            saveUnknown: 'The previous Save result is still unknown.',
            createFailed: 'Animation could not be created.',
            importFailed: 'Animation could not be imported.',
            layerLoadFailed: 'Animation "{name}" could not be loaded.',
            pastedLoadFailed: 'Pasted animation "{name}" could not be loaded.',
        },
        assets: {
            imagesAddFailed: 'Images could not be added.',
            imagesNotUploaded: '{count} image could not be uploaded. | {count} images could not be uploaded.',
            imageAddFailed: 'Image could not be added.',
            animationAddFailed: 'Animation could not be added.',
            animationLoadFailed: 'Animation could not be loaded.',
            notFound: 'This asset is not available in the current editor.',
            droppedImageFailed: 'Dropped image "{name}" could not be loaded.',
            droppedImageRejected: 'Dropped image "{name}" could not be loaded: {reason}',
        },
        codeImport: {
            noLayers: "Couldn't find any layer data.",
            fontNotFound: 'Font {font} was not found. Resetting to default.',
            imageSkipped: 'Image {name} was skipped. {reason}',
        },
        sidebar: {
            label: 'Editor sections',
            layers: 'Layers',
            images: 'Images',
            animations: 'Animations',
            fonts: 'Fonts',
            components: 'Components',
            prototype: 'Prototype',
        },
        appNav: {
            projects: 'Projects',
            assets: 'Assets',
        },
        publish: {
            fork: 'Fork',
            forkHint: 'Have a copy of this project',
            forkSignInHint: 'Sign in to fork this project',
            share: 'Share',
            copyPublicLink: 'Copy public link',
            linkCopied: 'Link was copied to clipboard',
            publishBoth: 'You need to publish both Project and Screen',
            publishProject: 'Publish in Project settings first',
            publishScreen: 'Publish the Screen first',
            presentation: 'Presentation',
            presentationUpgrade: 'Upgrade to Plus to enable presentation mode',
            saveAs: 'Save As',
            exportUpgrade: 'Upgrade to enable file export',
            textFile: 'Text file',
            pngImage: 'PNG image',
            bmpImage: 'BMP image',
            imageSaved: 'Image saved successfully',
            codeSaved: 'Source code saved successfully',
        },
        presenter: {
            screenImage: 'Screen image',
            thumbnail: 'Screen thumbnail',
            noImage: 'No image available',
            fullscreen: 'Fullscreen',
            exitFullscreen: 'Exit Fullscreen',
        },
    },
    tools: {
        undo: 'Undo',
        redo: 'Redo',
        select: 'Select',
        animation: 'Animation',
        arc: 'Arc',
        button: 'Button',
        checkbox: 'Checkbox',
        circle: 'Circle',
        ellipse: 'Ellipse',
        icon: 'Icon',
        image: 'Image',
        line: 'Line',
        paint: 'Paint',
        panel: 'Panel',
        polygon: 'Polygon',
        rect: 'Rectangle',
        slider: 'Slider',
        switch: 'Switch',
        text: 'Text',
        textArea: 'Text Area',
        triangle: 'Triangle',
    },
    layers: {
        emptyText: 'Empty text',
        unnamed: 'Unnamed layer',
    },
    code: {
        placeholder: 'Write your code here...',
        copySource: 'Copy source code',
        copied: 'Copied to clipboard',
        importHint: 'Import code - Experimental: Works best with code generated in Lopaka',
        syntax: 'Syntax',
        fonts: 'Fonts',
        images: 'Images',
        animations: 'Animations',
        onlyUsedGlyphs: 'Only render glyphs used',
        fontConvertFailed: 'Could not convert {font} to a U8g2 font header. No file was downloaded.',
        settings: {
            wrap: 'Wrapper function',
            progmem: 'Declare as PROGMEM',
            includeFonts: 'Include fonts',
            includeImages: 'Declare images',
            includeImageFiles: 'Include image files',
            includeAnimations: 'Animation helpers',
            declareVars: 'Declare variables',
            comments: 'Comments, layer titles',
            clearScreen: 'Clear/Fill display',
        },
    },
    shortcuts: {
        title: 'Shortcuts',
        move10px: 'move 10px',
        reorderLayers: 'reorder layers',
        copyPasteCut: 'copy/paste/cut',
        selectAll: 'select all',
        undo: 'undo',
        redo: 'redo',
        zoom: 'zoom in/out',
        multiSelect: 'multi-select',
        resetSelection: 'reset selection',
        groupLayers: 'group layers',
        ungroup: 'ungroup',
        resizing: 'Resizing',
        keepAspectRatio: 'Hold {key} to keep aspect ratio',
        resizeFromCenter: 'Hold {key} to resize from center',
        drawLine: 'Hold {key} to draw a line',
        eraser: 'eraser',
        polygonTriangle: 'Polygon, Triangle',
        placeVertices: 'place vertices',
        either: '{first} or {second}',
        finish: 'finish',
        editVertices: 'edit vertices',
        click: 'Click',
        rightClick: 'Right click',
        doubleClick: 'Double click',
    },
    galleryProject: {
        previewNotice: 'This is a public project in the Gallery. To continue editing, {fork} it into your projects.',
        fork: 'FORK',
        loadFailed: "Couldn't load this project. Check your connection and try again.",
        screens: 'Screens:',
        about: {
            heading: {
                design: 'About this design',
                display: 'About this display design',
                monochrome: 'About this monochrome display design',
                colorTft: 'About this color TFT display design',
                ePaper: 'About this e-paper display design',
            },
            stepsHeading: 'How to get the {platform} code for this screen',
            stepsHeadingDesign: 'How to use this design',
            summary:
                '{project} is a {platform} user interface for a {size} pixel display, shared by the Lopaka community.',
            viewingScreen: 'You are viewing the "{screen}" screen.',
            screensCount: 'The project has {count} screens; pick one in the Screens list.',
            platformNote: 'Lopaka generates {platform} code for this layout, ready to paste into your firmware.',
            platformNotes: {
                u8g2: 'U8g2 is a monochrome graphics library for Arduino, ESP32, and STM32 boards. It drives popular OLED and LCD controllers such as SSD1306, SH1106, and ST7920, and ships with hundreds of compact bitmap fonts.',
                tftEspi:
                    'TFT_eSPI is a fast graphics library for color TFT displays on ESP32, ESP8266, and RP2040 boards. It supports controllers such as ILI9341, ST7789, and ST7735, and draws in 16-bit RGB565 color.',
                adafruitGfx:
                    'Adafruit GFX is the core graphics library behind most Adafruit color display drivers for Arduino. The same drawing calls work across ST7735, ST7789, ILI9341, and many other color screens.',
                adafruitGfxMono:
                    'Adafruit GFX is the core graphics library behind most Adafruit display drivers for Arduino. In monochrome mode it pairs with drivers such as Adafruit_SSD1306 for small OLED screens.',
                arduinoGfx:
                    'Arduino GFX is a display library that supports a wide range of color controllers and data buses, from SPI TFT panels to parallel and RGB displays on ESP32-S3 boards.',
                lvgl: 'LVGL is an open-source embedded GUI library for microcontrollers. It provides widgets, styles, and animations for color displays on ESP32, STM32, and other boards with enough memory.',
                esphome:
                    'ESPHome builds firmware for ESP32 and ESP8266 devices from YAML configuration. Its display component renders lambdas like this one on OLED, TFT, and e-paper screens connected to Home Assistant.',
                micropython:
                    'MicroPython runs Python 3 directly on microcontrollers such as the ESP32 and Raspberry Pi Pico. Its framebuf module draws text, shapes, and bitmaps for small displays.',
                circuitpython:
                    'CircuitPython is an Adafruit fork of MicroPython for beginners. Its displayio library composes bitmaps, labels, and shapes for OLED, TFT, and e-paper displays.',
                flipper:
                    'Flipper Zero applications draw on its 128×64 monochrome screen through the Canvas API of the Flipper firmware. Designs made for it fit the device exactly.',
                inkplate:
                    'Inkplate boards combine an ESP32 with a recycled e-paper display. The Inkplate Arduino library draws text, shapes, and bitmaps in black and white or grayscale.',
                uint32: 'Raw uint32 output exports the design as plain pixel arrays, ready for a custom display driver or any framework that accepts raw bitmaps.',
                freestyle:
                    'Free drawing projects are pixel art with no target library. Fork one to reuse its layers in a project for your own display.',
                gxepd2: 'GxEPD2 is an Arduino library for SPI e-paper displays from Waveshare, Good Display, and others. It handles partial and full refreshes for low-power screens that keep their image without power.',
            },
            codeNote:
                'Each layer becomes its own drawing call in the exported {platform} code: text keeps its font, icons and images become bitmap arrays, and shapes become lines, rectangles, and circles, so the code stays readable and easy to change by hand.',
            codeNoteDesign:
                'Every layer stays editable after you fork it: text keeps its font, icons and images stay as separate bitmaps, and shapes stay as shapes you can move and resize.',
            steps: {
                copyCode:
                    'Copy the generated {platform} code from the code panel and paste it into your sketch or firmware.',
                codeSettings: 'Tune the code settings next to the panel, such as how images and fonts are exported.',
                star: 'Star the project to keep it in your Favourites tab of the Gallery.',
                fork: 'Fork the project to save your own copy in My Projects. Forking needs a free Lopaka account.',
                edit: 'Edit the text, shapes, icons, and images in your copy, and the code updates as you work.',
                canvas: 'Keep the {size} canvas for the same display, or resize it to fit your screen.',
                publish: 'Publish your version to the Gallery to share it with the community.',
            },
            guide: 'Read the {name} guide',
            platformsGuide: 'Read the platforms guide',
        },
    },
    imageWizard: {
        processing: {
            animationFrames: 'Processing animation frames…',
            animationFramesProgress: 'Processing animation frames ({completed}/{total})…',
            imageFrames: 'Processing image frames…',
            imageFramesProgress: 'Processing image frames ({completed}/{total})…',
            images: 'Processing images and adding layers to the Canvas...',
        },
        sourceType: {
            uploadFile: 'Upload file',
            byteArray: 'Input byte array',
        },
        upload: {
            dropHint: 'Choose files or drag & drop them here',
            browse: 'Browse files',
            supportedTypes: 'Supported file types',
            categories: {
                images: 'Images',
                gifs: 'GIFs',
                videos: 'Videos',
            },
        },
        bitmap: {
            placeholder: 'Paste the byte array here. Example: 0x0A, 0x0B, 0x09',
            width: 'Width:',
            height: 'Height:',
            swapBytes: 'Swap bytes',
        },
        import: 'Import',
        images: 'Images',
        imagesSelected: 'Images ({count})',
        preview: {
            videoTitle: 'Video preview',
            videoFrameAt: 'Preview video at {time}',
            gifTitle: 'Animation timeline',
            gifFrameAt: 'GIF frame at {time}',
        },
        metrics: {
            fps: 'Frames per second',
            duration: 'Duration',
            frames: 'Frames',
            frameSize: 'Frame size',
            estimatedSize: 'Estimated size',
            sourceFrames: 'Source frames',
            outputFrames: 'Output frames',
        },
        warnings: {
            fpsRange: 'Frames per second must be a whole number from 1 to 60.',
            videoFrameLimit: 'This setting creates {count} frames. Reduce FPS to stay within the {limit} frame limit.',
            gifFrameLimit: 'This setting creates more than {limit} frames. Reduce FPS to stay within the frame limit.',
            sizeLimit:
                'The estimated animation exceeds the {limit} animation size limit. Reduce FPS or the processed frame dimensions.',
        },
        mode: {
            label: 'Animated file import mode',
            frames: 'Frames',
            animation: 'Animation',
            toggle: 'Import as animation',
        },
        editor: {
            name: 'Name:',
            zoom: 'Zoom:',
            monochrome: 'Monochrome',
            rgb: 'RGB',
            byteSize: '{size} bytes',
            dimensions: 'Dimensions',
            widthShort: 'W',
            heightShort: 'H',
            fitScreen: 'Fit Screen',
            cropArea: 'Crop Area',
            brightness: 'Brightness',
            contrast: 'Contrast',
            grayscaleFirst: 'Grayscale first',
            invertPalette: 'Invert palette',
            invertResult: 'Invert result',
            resampling: {
                label: 'Resampling',
                highQuality: 'High quality',
                bilinear: 'Bilinear',
                nearest: 'Nearest',
            },
            dithering: {
                label: 'Dithering',
                floydSteinberg: 'Floyd-Steinberg',
                atkinson: 'Atkinson',
                bayer: 'Bayer 8x8',
                none: 'No dithering',
            },
        },
        errors: {
            loadFailed: 'Images could not be loaded. Check the files and try again.',
            processFailed: 'Images could not be processed. Try again.',
            videoProcessFailed: 'Video could not be processed. Try again or choose another video.',
            removeFailed: 'The image could not be removed from project storage. Refresh to retry.',
        },
    },
    icons: {
        currentProject: 'Current Project',
        upgradeBanner: 'Upgrade your subscription to get full access to icon packs',
        upgradeHint: 'Upgrade to get full access to icons',
        noCustomImages: 'No custom images or icons in this project',
        searchLabel: 'Search icons by name',
        clearSearch: 'Clear icon search',
        noResults: 'No icons found',
        seeMore: 'See more →',
    },
    animations: {
        categories: {
            ui: 'UI',
            web: 'Web',
            social: 'Social',
            media: 'Media',
            weather: 'Weather',
            ecommerce: 'Ecommerce',
            loaders: 'Loaders',
            emoji: 'Emoji',
            gestures: 'Gestures',
            food: 'Food',
            sports: 'Sports',
            custom: 'Custom',
        },
        detail: {
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        frameCount: '{count} frame | {count} frames',
        upgradeTooltip: 'Upgrade to get full access',
        deleteCustomWarning:
            'Delete this custom animation? All layers using this animation in the current project will become unavailable.',
        searchLabel: 'Search animations by name',
        clearSearch: 'Clear search',
        createHint: 'Add new custom animation',
        createNew: 'Create new',
        noResults: 'No animations found',
        custom: {
            loading: 'Loading saved animations...',
            loadFailed: 'Saved animations are unavailable. Select Custom again to retry.',
            empty: 'No saved animations',
            use: 'Use saved animation {id}',
            unavailableLabel: 'Saved animation unavailable',
            unavailable: 'Unavailable',
        },
    },
    assets: {
        title: 'Assets',
        stats: {
            total: 'Total',
            images: 'Images',
            fonts: 'Fonts',
            remaining: 'Remaining',
            unlimited: 'Unlimited',
        },
        upgrade: 'Upgrade to add more',
        empty: 'No assets uploaded yet.',
        emptyHint: 'Upload images and fonts from the editor to see them here.',
        count: '{count} asset | {count} assets',
        delete: 'Delete {name}',
        untitledProject: 'Project {id}',
    },
    // Chrome of the VitePress documentation (pages/.vitepress/docs-locales.ts): navigation,
    // sidebar, and the default theme's labels. The docs pages themselves are markdown.
    docsSite: {
        openApp: 'Open Lopaka App',
        sidebar: {
            documentation: 'Documentation',
            overview: 'Overview',
            gettingStarted: 'Getting Started',
            platforms: 'Platforms',
            editorOverview: 'Editor Overview',
            importingImages: 'Importing Images',
            animations: 'Animations',
            fonts: 'Fonts',
            codeGeneration: 'Code Generation',
            keyboardShortcuts: 'Keyboard Shortcuts',
            subscription: 'Subscription',
            plansDescription: 'Plans Description',
            platformGuides: 'Platform Guides',
        },
        outlineLabel: 'On this page',
        previousPage: 'Previous page',
        nextPage: 'Next page',
        langMenuLabel: 'Change language',
        sidebarMenuLabel: 'Menu',
        returnToTopLabel: 'Return to top',
        darkModeSwitchLabel: 'Appearance',
        lightModeSwitchTitle: 'Switch to light theme',
        darkModeSwitchTitle: 'Switch to dark theme',
        skipToContentLabel: 'Skip to content',
        notFound: {
            title: 'PAGE NOT FOUND',
            quote: "But if you don't change your direction, and if you keep looking, you may end up where you are heading.",
            linkLabel: 'go to home',
            linkText: 'Take me home',
        },
        search: {
            buttonText: 'Search',
            displayDetails: 'Display detailed list',
            resetButtonTitle: 'Reset search',
            backButtonTitle: 'Close search',
            noResultsText: 'No results for',
            selectText: 'to select',
            selectKeyAriaLabel: 'enter',
            navigateText: 'to navigate',
            navigateUpKeyAriaLabel: 'up arrow',
            navigateDownKeyAriaLabel: 'down arrow',
            closeText: 'to close',
            closeKeyAriaLabel: 'escape',
        },
    },
};

export type Messages = typeof en;

export default en;
